import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import {
  getUaRoiUaMediaScopeSelectorFingerprint,
  mergeUaRoiUaMediaScopeIntoFormattedFilters,
  type TKpiOverviewDataScope,
} from '../../../utils/uaRoiUaMediaScopeFilters';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { useCallback, useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import {
  useKpiEventMetricGameCode,
  useKpiEventMetricServicePayload,
} from '../../KpiEventMetricTrendService/_hooks';
import {
  kpiDimensionMetricsTrendBeforeRequest,
  kpiDimensionMetricsTrendBeforeRequestWithAllowlists,
  normalizeDimensionMetricsTrendResponse,
  resolveCampaignFilterListForAllowlist,
  resolveOsListForDimensionAllowlist,
  resolveUaNetworkFilterListForAllowlist,
} from '../_utils';
import type {
  IKpiDimensionReqData,
  IKpiEventDimensionTrendServiceOuterResult,
  IKpiEventDimensionTrendServiceProps,
} from '../_type';
import { DIMENSION_DEFAULT_API, DIMENSION_DEFAULT_TRACK_DATA } from '../_type';

export const useKpiEventDimensionTrendServiceOuter = (
  props: IKpiEventDimensionTrendServiceProps,
): IKpiEventDimensionTrendServiceOuterResult => {
  const {
    style,
    className,
    config,
    children,
    data: propsData,
    eventData: rawEventList,
    loading: eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    api: apiProp,
    dataKey: dataKeyProp,
    trackData: trackDataProp,
    beforeRequest: beforeRequestProp,
    formatData: formatDataProp,
    metrics: metricsProp,
    topNum: topNumProp,
    dimensionKey: dimensionKeyProp,
    logicalFlag: logicalFlagProp,
    accumulation: accumulationProp,
    valueFormatRule: valueFormatRuleProp,
    selectorMapKey: selectorMapKeyProp,
    dimensionValueSource: dimensionValueSourceProp,
    dimensionRowField: dimensionRowFieldProp,
    multiSort: multiSortProp,
    metricResponseKey: metricResponseKeyProp,
    contentHeader,
    dimensionSecondaryRowField: dimensionSecondaryRowFieldProp,
    dimensionLegendCompositeKeys: dimensionLegendCompositeKeysProp,
    mergeAnomalyIntoTrend: mergeAnomalyIntoTrendProp,
    anomalyMetricKey: anomalyMetricKeyProp,
    allowedCampaignCodes: allowedCampaignCodesProp,
    allowedOsCodes: allowedOsCodesProp,
    allowedUaNetworkCodes: allowedUaNetworkCodesProp,
    campaignMatchType: campaignMatchTypeProp,
    campaignSelectorKey: campaignSelectorKeyProp,
    uaNetworkMatchType: uaNetworkMatchTypeProp,
    uaNetworkMatchTypeNot: uaNetworkMatchTypeNotProp,
    uaNetworkSelectorKey: uaNetworkSelectorKeyProp,
    downloadIncludeLastWeek: downloadIncludeLastWeekProp,
    dimensionSeriesOrderPriority: dimensionSeriesOrderPriorityProp,
    dimensionSeriesColorMap: dimensionSeriesColorMapProp,
    hideWhenKpiOverviewScope: hideWhenKpiOverviewScopeProp,
    kpiOverviewDataScope: kpiOverviewDataScopeProp,
    applyKpiUaRoiUaMediaScope: applyKpiUaRoiUaMediaScopeProp,
  } = props || {};
  const mergeAnomalyIntoTrend =
    mergeAnomalyIntoTrendProp ?? config?.mergeAnomalyIntoTrend ?? true;
  const anomalyMetricKey =
    anomalyMetricKeyProp ?? config?.anomalyMetricKey;

  const applyKpiUaRoiUaMediaScope =
    applyKpiUaRoiUaMediaScopeProp ??
    (config?.applyKpiUaRoiUaMediaScope as boolean | undefined);

  const kpiOverviewScope =
    kpiOverviewDataScopeProp ??
    (config?.kpiOverviewDataScope as TKpiOverviewDataScope | undefined) ??
    (propsData as { kpiOverviewDataScope?: TKpiOverviewDataScope; } | undefined)
      ?.kpiOverviewDataScope ??
    'all';

  const api = apiProp ?? config?.api ?? DIMENSION_DEFAULT_API;
  const dataKey = (dataKeyProp ?? config?.dataKey ?? 'trendData') as string;
  const trackData = useMemo(() => {
    const base = (trackDataProp ??
      config?.trackData ??
      DIMENSION_DEFAULT_TRACK_DATA) as string[];
    if (applyKpiUaRoiUaMediaScope) {
      return [...base, '_kpiOverviewScopeToken'];
    }
    return base;
  }, [trackDataProp, config?.trackData, applyKpiUaRoiUaMediaScope]);
  const allowedOsCodes = allowedOsCodesProp ?? config?.allowedOsCodes;

  const campaignMatchType = (campaignMatchTypeProp ?? config?.campaignMatchType) as
    | string
    | undefined;
  const campaignSelectorKey = (campaignSelectorKeyProp ??
    config?.campaignSelectorKey ??
    'campaign') as string;

  const uaNetworkMatchType = (uaNetworkMatchTypeProp ?? config?.uaNetworkMatchType) as
    (string[] | undefined);
  const uaNetworkMatchTypeNot = (uaNetworkMatchTypeNotProp ??
    config?.uaNetworkMatchTypeNot) as (string[] | undefined);
  const uaNetworkSelectorKey = (uaNetworkSelectorKeyProp ??
    config?.uaNetworkSelectorKey ??
    'ua_network') as string;

  const selector = useSelector(
    (s: { dashboardSetting?: { selector?: Record<string, unknown>; }; }) =>
      s?.dashboardSetting?.selector,
  ) as Record<string, unknown> | undefined;

  const { selector: uaScopeSelector } = useSelector(
    (state: { dashboardSetting?: { selector?: Record<string, Array<Record<string, unknown>>>; }; }) => ({
      selector: state?.dashboardSetting?.selector as
        | Record<string, Array<Record<string, unknown>>>
        | undefined,
    }),
    shallowEqual,
  );

  const allowedCampaignCodes = useMemo(() => {
    const staticCodes = allowedCampaignCodesProp ?? config?.allowedCampaignCodes;
    if (Array.isArray(staticCodes) && staticCodes.length > 0) {
      return staticCodes;
    }
    if (!campaignMatchType || !selector) {
      return undefined;
    }
    const list = (selector[campaignSelectorKey] || []) as Array<{
      type?: string;
      code?: string;
    }>;
    const codes = list
      .filter(item => item?.type === campaignMatchType)
      .map(item => item?.code)
      .filter(Boolean) as string[];
    return codes.length > 0 ? codes : undefined;
  }, [
    allowedCampaignCodesProp,
    config?.allowedCampaignCodes,
    campaignMatchType,
    campaignSelectorKey,
    selector,
  ]);

  const allowedUaNetworkCodes = useMemo(() => {
    const staticCodes = allowedUaNetworkCodesProp ?? config?.allowedUaNetworkCodes;
    if (Array.isArray(staticCodes) && staticCodes.length > 0) {
      return staticCodes;
    }
    if (!selector) {
      return undefined;
    }
    const list = (selector[uaNetworkSelectorKey] || []) as Array<{
      type?: string;
      code?: string;
    }>;
    if (Array.isArray(uaNetworkMatchType) && uaNetworkMatchType.length > 0) {
      const includeSet = new Set(uaNetworkMatchType.map(t => String(t)));
      const codes = list
        .filter(item => item?.type && includeSet.has(String(item.type)))
        .map(item => item?.code)
        .filter(Boolean) as string[];
      return codes.length > 0 ? codes : undefined;
    }
    if (
      !Array.isArray(uaNetworkMatchTypeNot) ||
      uaNetworkMatchTypeNot.length === 0
    ) {
      return undefined;
    }
    const excludeSet = new Set(uaNetworkMatchTypeNot);
    const codesExclude = list
      .filter(item => item?.type && !excludeSet.has(item.type))
      .map(item => item?.code)
      .filter(Boolean) as string[];
    return codesExclude.length > 0 ? codesExclude : undefined;
  }, [
    allowedUaNetworkCodesProp,
    config?.allowedUaNetworkCodes,
    uaNetworkMatchType,
    uaNetworkMatchTypeNot,
    uaNetworkSelectorKey,
    selector,
  ]);

  const { filterVal: ctxFilterVal } = useContext(FilterContext);

  const forceNoData = useMemo(() => {
    if (!allowedCampaignCodes && !allowedOsCodes && !allowedUaNetworkCodes) {
      return false;
    }
    const fv = ctxFilterVal as Record<string, unknown> | undefined;
    if (allowedOsCodes) {
      const osList = resolveOsListForDimensionAllowlist(fv, allowedOsCodes as string[]);
      if (osList === null) {
        return true;
      }
    }
    if (allowedCampaignCodes) {
      const campaignList = resolveCampaignFilterListForAllowlist(
        fv,
        allowedCampaignCodes as string[],
      );
      if (campaignList === null) {
        return true;
      }
    }
    if (allowedUaNetworkCodes) {
      const uaNetworkList = resolveUaNetworkFilterListForAllowlist(
        fv,
        allowedUaNetworkCodes as string[],
      );
      if (uaNetworkList === null) {
        return true;
      }
    }
    return false;
  }, [allowedCampaignCodes, allowedOsCodes, allowedUaNetworkCodes, ctxFilterVal]);

  const skipPayload = useMemo(() => ({ _dimSkipRequest: true as const }), []);

  const beforeRequest = useCallback(
    (data: IKpiDimensionReqData) => {
      const resolveBuildBase = (): ((
        d: IKpiDimensionReqData,
      ) => Record<string, unknown> | false) => {
        if (typeof beforeRequestProp === 'function') {
          return beforeRequestProp as (d: IKpiDimensionReqData) => Record<string, unknown> | false;
        }
        if (typeof beforeRequestProp === 'string') {
          return stringToFunction(beforeRequestProp) as (
            d: IKpiDimensionReqData,
          ) => Record<string, unknown> | false;
        }
        if (typeof config?.beforeRequest === 'function') {
          return config.beforeRequest as (d: IKpiDimensionReqData) => Record<string, unknown> | false;
        }
        if (typeof config?.beforeRequest === 'string') {
          return stringToFunction(config.beforeRequest as string) as (
            d: IKpiDimensionReqData,
          ) => Record<string, unknown> | false;
        }
        return kpiDimensionMetricsTrendBeforeRequest;
      };
      const result = kpiDimensionMetricsTrendBeforeRequestWithAllowlists(
        data,
        resolveBuildBase(),
      );
      if (result === false) {
        return skipPayload;
      }
      if (
        !applyKpiUaRoiUaMediaScope ||
        kpiOverviewScope !== 'ua_roi_ua_media'
      ) {
        return result;
      }
      const filters = result.filters as Record<string, unknown> | undefined;
      if (!filters || typeof filters !== 'object') {
        return result;
      }
      const merged = mergeUaRoiUaMediaScopeIntoFormattedFilters(
        filters,
        data.filterVal as Record<string, unknown> | undefined,
        uaScopeSelector,
      );
      if (merged === false) {
        return skipPayload;
      }
      return { ...result, filters: merged };
    },
    [
      beforeRequestProp,
      config?.beforeRequest,
      skipPayload,
      applyKpiUaRoiUaMediaScope,
      kpiOverviewScope,
      uaScopeSelector,
    ],
  );

  const { gameCode } = useKpiEventMetricGameCode();
  const metrics = metricsProp ?? config?.metrics ?? [];
  const topNum = topNumProp ?? config?.topNum ?? 10;
  const dimensionKey = dimensionKeyProp ?? config?.dimensionKey ?? 'ua_network';
  const selectorMapKey =
    selectorMapKeyProp ?? config?.selectorMapKey ?? dimensionKey;
  const logicalFlag =
    logicalFlagProp ?? config?.logicalFlag ?? 'show_all_data';
  const accumulation = accumulationProp ?? config?.accumulation;
  const valueFormatRule =
    valueFormatRuleProp ?? config?.valueFormatRule ?? 'thousands2Decimal';
  const dimensionValueSource =
    dimensionValueSourceProp ??
    config?.dimensionValueSource ??
    'custom_dimensions';
  const dimensionRowField =
    dimensionRowFieldProp ?? config?.dimensionRowField;
  const dimensionSecondaryRowField =
    dimensionSecondaryRowFieldProp ?? config?.dimensionSecondaryRowField;
  const dimensionLegendCompositeKeys =
    dimensionLegendCompositeKeysProp ?? config?.dimensionLegendCompositeKeys;
  const multiSort = multiSortProp ?? config?.multiSort;
  const metricResponseKey =
    metricResponseKeyProp ?? config?.metricResponseKey;
  const downloadIncludeLastWeek =
    downloadIncludeLastWeekProp ?? config?.downloadIncludeLastWeek;

  const dimensionSeriesOrderPriority = useMemo(() => {
    const raw =
      dimensionSeriesOrderPriorityProp ??
      config?.dimensionSeriesOrderPriority;
    if (!Array.isArray(raw) || raw.length === 0) {
      return undefined;
    }
    const cleaned = raw
      .map((x: unknown) => String(x ?? '').trim())
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : undefined;
  }, [dimensionSeriesOrderPriorityProp, config?.dimensionSeriesOrderPriority]);

  const hideWhenKpiOverviewScope =
    hideWhenKpiOverviewScopeProp ??
    (config?.hideWhenKpiOverviewScope as TKpiOverviewDataScope | undefined);

  const dimensionSeriesColorMap = useMemo(() => {
    const raw =
      dimensionSeriesColorMapProp ?? config?.dimensionSeriesColorMap;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return undefined;
    }
    const out: Record<string, string> = {};
    Object.keys(raw as Record<string, unknown>).forEach(k => {
      const ck = String(k).trim();
      const cv = (raw as Record<string, unknown>)[k];
      if (!ck || typeof cv !== 'string' || !cv.trim()) {
        return;
      }
      out[ck] = cv.trim();
    });
    return Object.keys(out).length > 0 ? out : undefined;
  }, [dimensionSeriesColorMapProp, config?.dimensionSeriesColorMap]);

  const wrappedApi = useMemo(() => {
    const resolveEmpty = () => Promise.resolve([]);
    if (typeof api === 'function') {
      return (params: Record<string, unknown>, d: unknown) => {
        if (params?._dimSkipRequest) {
          return resolveEmpty();
        }
        return (api as (p: Record<string, unknown>, d: unknown) => Promise<unknown>)(
          params,
          d,
        );
      };
    }
    const url = String(api);
    return (params: Record<string, unknown>) => {
      if (params?._dimSkipRequest) {
        return resolveEmpty();
      }
      return dashboardRequest({ url, method: 'post', data: params });
    };
  }, [api]);

  const formatData = useMemo(() => {
    if (formatDataProp) {
      return formatDataProp;
    }
    if (config?.formatData) {
      return config.formatData;
    }
    const m = (Array.isArray(metrics) ? metrics : []) as string[];
    return (raw: unknown) =>
      normalizeDimensionMetricsTrendResponse(raw, m, {
        dimensionKey: dimensionKey as string,
        dimensionValueSource: dimensionValueSource as
          | 'custom_dimensions'
          | 'row_field'
          | undefined,
        dimensionRowField: dimensionRowField as string | undefined,
        dimensionSecondaryRowField: dimensionSecondaryRowField as
          | string
          | undefined,
        maxSeries: topNum as number,
        valueFormatRule: valueFormatRule as string,
        metricResponseKey: metricResponseKey as string | undefined,
        dimensionSeriesOrderPriority,
        dimensionSeriesColorMap,
      });
  }, [
    formatDataProp,
    config?.formatData,
    metrics,
    dimensionKey,
    dimensionValueSource,
    dimensionRowField,
    dimensionSecondaryRowField,
    topNum,
    valueFormatRule,
    metricResponseKey,
    dimensionSeriesOrderPriority,
    dimensionSeriesColorMap,
  ]);

  const data = useKpiEventMetricServicePayload(gameCode, {
    ...(propsData || {}),
    metrics: Array.isArray(metrics) ? metrics : [],
    topNum,
    dimensionKey,
    logicalFlag,
    accumulation,
    dimensionValueSource,
    dimensionRowField,
    selectorMapKey,
    multiSort,
    metricResponseKey,
    allowedCampaignCodes,
    allowedOsCodes,
    allowedUaNetworkCodes,
    dimensionSeriesOrderPriority,
    dimensionSeriesColorMap,
    ...(applyKpiUaRoiUaMediaScope
      ? { _kpiOverviewScopeToken: kpiOverviewScope }
      : {}),
  });

  const serviceMountKey = useMemo(() => {
    if (!applyKpiUaRoiUaMediaScope) {
      return 'kpi-event-dimension-trend';
    }
    if (kpiOverviewScope !== 'ua_roi_ua_media') {
      return `kpi-event-dimension-trend-${kpiOverviewScope}`;
    }
    return `kpi-event-dimension-trend-${kpiOverviewScope}-${getUaRoiUaMediaScopeSelectorFingerprint(
      uaScopeSelector,
    )}`;
  }, [applyKpiUaRoiUaMediaScope, kpiOverviewScope, uaScopeSelector]);

  const hideBlock =
    Boolean(hideWhenKpiOverviewScope) &&
    kpiOverviewScope === hideWhenKpiOverviewScope;

  return {
    hideBlock,
    className,
    style,
    serviceMountKey,
    wrappedApi: wrappedApi as string | ((data: unknown, outParame?: unknown) => void),
    data: data as Record<string, unknown>,
    dataKey,
    trackData,
    beforeRequest: beforeRequest as (data: IKpiDimensionReqData) => unknown,
    formatData: formatData as string | ((data: unknown, filterVal?: unknown) => unknown),
    innerProps: {
      selectorMapKey: selectorMapKey as string,
      dimensionLegendCompositeKeys,
      contentHeader,
      downloadIncludeLastWeek: downloadIncludeLastWeek as boolean | undefined,
      eventData: rawEventList,
      loading: eventListLoading,
      anomalyResult,
      anomalyResultError,
      anomalyResultLoading,
      mergeAnomalyIntoTrend: mergeAnomalyIntoTrend as boolean | undefined,
      anomalyMetricKey: anomalyMetricKey as string | undefined,
      kpiOverviewDataScope: kpiOverviewScope,
      forceNoData,
      children: children ?? null,
    },
  };
};
